import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import {
  ArrowLeft,
  Gamepad2,
  History,
  Sparkles,
  Swords,
  User,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  listEvents,
  listEventPlayerResults,
  listEventTopCutResults,
  listLeagues,
  listLeaguePointAdjustments,
  listPlayerConstructs,
} from "../graphql/queries";
import {
  getLeagueGame,
  getLeagueGameLabel,
  LEAGUE_GAMES,
  LeagueGame,
} from "../lib/leagueGames";
import {
  aggregateLeaguePlayerStats,
  calculateWinRate,
  LeaguePlayerSummary,
} from "../lib/playerStats";

const client = generateClient();

const PlayerHistory: React.FC = () => {
  const { playerConstructId } = useParams<{ playerConstructId: string }>();
  const { data: leagues } = useQuery({
    queryKey: ["leagues"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeagues,
        variables: { limit: 100 },
      });
      return result.data.listLeagues.items;
    },
  });
  const { data: events } = useQuery({
    queryKey: ["allEvents"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEvents,
        variables: { limit: 1000 },
      });
      return result.data.listEvents.items;
    },
  });
  const { data: results } = useQuery({
    queryKey: ["allEventPlayerResults"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventPlayerResults,
        variables: { limit: 1000 },
      });
      return result.data.listEventPlayerResults.items;
    },
  });
  const { data: topCutResults } = useQuery({
    queryKey: ["allTopCutResults"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventTopCutResults,
        variables: { limit: 1000 },
      });
      return result.data.listEventTopCutResults.items;
    },
  });
  const { data: constructs } = useQuery({
    queryKey: ["allPlayerConstructs"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return result.data.listPlayerConstructs.items;
    },
  });
  const { data: adjustments } = useQuery({
    queryKey: ["allLeaguePointAdjustments"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeaguePointAdjustments,
        variables: { limit: 1000 },
      });
      return result.data.listLeaguePointAdjustments.items;
    },
  });

  const leagueSummaries = useMemo(
    () =>
      (leagues || []).flatMap((league: any) =>
        aggregateLeaguePlayerStats(
          league,
          events || [],
          results || [],
          topCutResults || [],
          adjustments || []
        ).map((summary) => ({ league, summary }))
      ),
    [adjustments, events, leagues, results, topCutResults]
  );
  const selectedConstruct = constructs?.find(
    (construct: any) => construct.id === playerConstructId
  );
  const selectedHistory = leagueSummaries.filter(
    ({ summary }) => summary.playerConstructId === playerConstructId
  );

  const careerPlayers = useMemo(() => {
    const grouped = new Map<
      string,
      LeaguePlayerSummary & {
        percentileTotal: number;
        rankTotal: number;
        leaguesPlayed: number;
      }
    >();

    leagueSummaries.forEach(({ summary }) => {
      const existing = grouped.get(summary.playerConstructId) || {
        ...summary,
        totalPoints: 0,
        totalEvents: 0,
        totalWins: 0,
        totalLosses: 0,
        totalDraws: 0,
        bestRank: Number.POSITIVE_INFINITY,
        averageRank: 0,
        averageFinishPercentile: 0,
        currentRank: 0,
        percentileTotal: 0,
        rankTotal: 0,
        leaguesPlayed: 0,
      };
      existing.totalPoints += summary.totalPoints;
      existing.totalEvents += summary.totalEvents;
      existing.totalWins += summary.totalWins;
      existing.totalLosses += summary.totalLosses;
      existing.totalDraws += summary.totalDraws;
      existing.bestRank = Math.min(existing.bestRank, summary.bestRank);
      existing.rankTotal += summary.averageRank * summary.totalEvents;
      existing.percentileTotal +=
        summary.averageFinishPercentile * summary.totalEvents;
      existing.leaguesPlayed += 1;
      grouped.set(summary.playerConstructId, existing);
    });

    return Array.from(grouped.values())
      .map((summary) => ({
        ...summary,
        averageRank: summary.rankTotal / summary.totalEvents,
        averageFinishPercentile: summary.percentileTotal / summary.totalEvents,
        construct: constructs?.find(
          (construct: any) => construct.id === summary.playerConstructId
        ),
      }))
      .sort((a, b) => b.totalEvents - a.totalEvents);
  }, [constructs, leagueSummaries]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="outline" size="sm">
          <Link to="/players">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Active Players
          </Link>
        </Button>
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <History className="h-7 w-7 text-primary" />
            {selectedConstruct
              ? `${selectedConstruct.name} Career`
              : "Player Career History"}
          </h1>
          <p className="text-muted-foreground">
            Statistics compartmentalized by game, league, and season
          </p>
        </div>
      </div>

      {playerConstructId ? (
        selectedHistory.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No historical results were found for this player.
            </CardContent>
          </Card>
        ) : (
          (Object.keys(LEAGUE_GAMES) as LeagueGame[]).map((game) => {
            const gameHistory = selectedHistory.filter(
              ({ league }) => getLeagueGame(league.game) === game
            );
            if (gameHistory.length === 0) return null;
            const Icon =
              game === "MAGIC_THE_GATHERING"
                ? Sparkles
                : game === "RIFTBOUND"
                ? Swords
                : Gamepad2;

            return (
              <section key={game} className="space-y-3">
                <h2 className="flex items-center gap-2 text-xl font-semibold">
                  <Icon className="h-5 w-5 text-primary" />
                  {getLeagueGameLabel(game)}
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {gameHistory.map(({ league, summary }) => (
                    <Card key={league.id}>
                      <CardHeader>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <CardTitle>{league.name}</CardTitle>
                            <p className="text-sm text-muted-foreground">
                              {league.format} · {league.startDate} to{" "}
                              {league.endDate || "ongoing"}
                            </p>
                          </div>
                          <Badge>{summary.totalPoints} pts</Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4 text-sm">
                        <Stat label="Events" value={summary.totalEvents} />
                        <Stat
                          label="Record"
                          value={`${summary.totalWins}-${summary.totalLosses}-${summary.totalDraws}`}
                        />
                        <Stat
                          label={
                            league.format === "Commander"
                              ? "Pod win rate"
                              : "League win rate"
                          }
                          value={`${calculateWinRate(
                            summary.totalWins,
                            summary.totalLosses,
                            summary.totalDraws
                          ).toFixed(1)}%`}
                        />
                        <Stat
                          label="Average placement"
                          value={summary.averageRank.toFixed(1)}
                        />
                        <Stat
                          label="Finish percentile"
                          value={`${summary.averageFinishPercentile.toFixed(
                            1
                          )}%`}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            );
          })
        )
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {careerPlayers.map((player) => (
            <Card key={player.playerConstructId}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <User className="h-5 w-5 text-primary" />
                  </span>
                  <div>
                    <CardTitle>
                      {player.construct?.name || "Unknown player"}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {player.leaguesPlayed} leagues · {player.totalEvents}{" "}
                      events
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Stat label="Career points" value={player.totalPoints} />
                  <Stat
                    label="Finish percentile"
                    value={`${player.averageFinishPercentile.toFixed(1)}%`}
                  />
                  <Stat
                    label="Career win rate"
                    value={`${calculateWinRate(
                      player.totalWins,
                      player.totalLosses,
                      player.totalDraws
                    ).toFixed(1)}%`}
                  />
                </div>
                <Button asChild variant="outline" className="w-full">
                  <Link to={`/players/${player.playerConstructId}/history`}>
                    View league history
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <p className="text-muted-foreground">{label}</p>
    <p className="font-semibold">{value}</p>
  </div>
);

export default PlayerHistory;
